/**
 * Evaluate a quantity formula such as `Math.ceil({breedte}) + Math.ceil({lengte})`.
 * Missing or non-numeric placeholders become 0, so an empty optional field
 * (a hidden doek, an off toggle) cannot crash the whole expression.
 *
 * KEEP IN SYNC: copied as smartflow-api/utils/quantityFormula.js. Change both.
 */

const MATH_FN = /Math\.(ceil|round|floor|abs|min|max)/g;
const SAFE_AFTER_MATH = /^[0-9+\-*/().\s]+$/;

export function toQuantityNumber(value) {
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = parseFloat(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {string} formula
 * @param {(key: string) => unknown} resolveValue
 * @returns {number|null} null when the formula is empty or unsafe
 */
export function evaluateNumericFormula(formula, resolveValue) {
  if (!formula || typeof formula !== 'string') return null;

  let expression = formula;
  const placeholders = formula.match(/\{([^}]+)\}/g) || [];
  const seen = new Set();

  placeholders.forEach((placeholder) => {
    if (seen.has(placeholder)) return;
    seen.add(placeholder);
    const key = placeholder.slice(1, -1);
    const numeric = toQuantityNumber(resolveValue(key));
    expression = expression.split(placeholder).join(numeric === null ? '0' : String(numeric));
  });

  const withoutMath = expression.replace(MATH_FN, '');
  if (!SAFE_AFTER_MATH.test(withoutMath)) {
    throw new Error('Unsafe quantity formula');
  }

  // eslint-disable-next-line no-new-func
  const result = Function(`"use strict"; return (${expression})`)();
  return Number.isFinite(result) ? result : null;
}
